import React, { useMemo } from 'react';
import { useStore } from 'components/Store';

const MAX_VISIBLE_CROP_SEASONS = 2;

interface DrawerHeaderProps {
  /** Record-type badge, sized 16x16 by the caller. */
  icon: React.ReactNode;
  typeLabel: string;
  title: string;
  cropSeasonIds?: string[];
}

/**
 * Type row, record name and crop season, spanning 32-104 in the design. The
 * 32px bottom padding is the gap to the tab strip.
 */
const DrawerHeader = ({
  icon,
  typeLabel,
  title,
  cropSeasonIds
}: DrawerHeaderProps) => {
  const {
    state: { cropSeasons }
  } = useStore();

  const cropSeasonLabel = useMemo(() => {
    if (!cropSeasonIds?.length || !cropSeasons?.length) return '';

    const names = cropSeasonIds
      .map((id: string) => cropSeasons.find((cs: any) => cs.id === id)?.name)
      .filter(Boolean) as string[];

    if (!names.length) return '';

    const visible = names.slice(0, MAX_VISIBLE_CROP_SEASONS);
    const remaining = names.length - visible.length;

    return remaining > 0
      ? `${visible.join(' | ')} +${remaining}`
      : visible.join(' | ');
  }, [cropSeasonIds, cropSeasons]);

  return (
    <div data-testid="linked-record-drawer-header" className="shrink-0 py-8">
      <div className="flex items-center gap-1">
        <span className="shrink-0 flex items-center">{icon}</span>
        <div className="flex items-center gap-1.5">
          <span
            data-testid="linked-record-drawer-type-label"
            className="text-xs leading-5 font-semibold text-neutral-60"
          >
            {typeLabel}
          </span>
          <span className="text-neutral-40 leading-5">|</span>
          <span className="text-xs leading-5 font-semibold text-neutral-60">
            OVERVIEW
          </span>
        </div>
      </div>

      <div
        data-testid="linked-record-drawer-title"
        className="text-xl leading-7 font-semibold text-neutral-1000 truncate"
        title={title}
      >
        {title || '---'}
      </div>

      <div
        data-testid="linked-record-drawer-crop-season"
        className="mt-1 text-sm leading-5 text-neutral-60 truncate"
        title={cropSeasonLabel || undefined}
      >
        {cropSeasonLabel || '---'}
      </div>
    </div>
  );
};

export default DrawerHeader;
